// Machine selection for PinGolf lineups. Mirrors the server's balancing in
// pingolfSeasonService.randomizeEmptyWeekLineups, so the per-week "Randomize 9 holes"
// button doesn't undo the even spread a generated season starts with.

export const HOLES_PER_WEEK = 9;

// Only operational machines are auto-picked. Everything else on the floor
// (needs_attention, out_of_order, in_tournament) can still be chosen by hand for a hole,
// but a randomizer that grabs a machine nobody can play makes for a dead hole.
export const AUTO_PICK_STATUS = 'operational';

export const isAutoPickable = (machine) => machine?.status === AUTO_PICK_STATUS;

// Uniform shuffle, Fisher-Yates. `sort(() => Math.random() - 0.5)` is not uniform - it
// leaves items biased toward the order they came in.
export const shuffle = (items) => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// How many holes each machine already fills across the given weeks, keyed by String(id)
// because a lineup's machineId can arrive as either a number (server) or a string (form).
export const countMachineUsage = (weeks) => {
  const counts = new Map();
  for (const week of weeks || []) {
    for (const hole of week?.lineup || []) {
      const key = String(hole.machineId);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
  }
  return counts;
};

// Picks `holeCount` machines, favouring the ones used least across the season so far and
// breaking ties at random. Drawing each week independently is what left seasons lopsided -
// one machine in nearly every week, another in none.
export const pickBalancedMachines = (machines, usage, holeCount = HOLES_PER_WEEK) => {
  const picked = shuffle(machines)
    .sort((a, b) => (usage.get(String(a.id)) || 0) - (usage.get(String(b.id)) || 0))
    .slice(0, Math.min(holeCount, machines.length));
  // Shuffled again so hole 1 isn't the least-used machine every time.
  return shuffle(picked);
};

const plural = (count, singular, pluralForm = `${singular}s`) => `${count} ${count === 1 ? singular : pluralForm}`;

// The toast after a machine target change or a season's "Refresh targets from machines". The
// server pushes a machine's target into every upcoming hole and week lineup that can still
// change and reports counts; a hole with its own target is left alone and said so, so nobody
// wonders why one night did not follow. null when there is nothing worth saying.
export const describeTargetRefresh = (counts, { announceNoChange = false } = {}) => {
  if (!counts) return null;
  const holes = Number(counts.holesUpdated) || 0;
  const weeks = Number(counts.weeksUpdated) || 0;
  const overridden = Number(counts.overriddenSkipped) || 0;
  const courses = Number(counts.coursesBuilt) || 0;

  const changed = [];
  if (holes > 0) changed.push(plural(holes, 'upcoming PinGolf hole'));
  if (weeks > 0) changed.push(plural(weeks, 'week lineup'));

  let message = changed.length > 0
    ? `Updated the target on ${changed.join(' and ')}.`
    : '';
  if (courses > 0) {
    message += `${message ? ' ' : ''}Built the course for ${plural(courses, 'night')} that had been waiting on a target.`;
  }
  if (overridden > 0) {
    message += `${message ? ' ' : ''}${plural(overridden, 'hole')} with its own target ${overridden === 1 ? 'was' : 'were'} left alone.`;
  }

  if (!message) {
    return announceNoChange ? 'Every upcoming hole already matches its machine target.' : null;
  }
  return message;
};
